import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { useIntl } from 'react-intl'

import { onSessionEnd, sessionEnd, sessionIdleTimeout } from '@/shared/api/session-end'
import { Button, Dialog } from '@/shared/ui'

/** How long before the session's idle end the warning shows, unless the idle time is short. */
export const idleWarningLeadMs = 2 * 60 * 1000

/** How long after the API could not be reached at the end it is asked again. */
export const idleCheckRetryMs = 15_000

/** When screen readers hear the countdown again, besides when the warning opens. */
const lastCallSeconds = 30

// The longest the timer sleeps before it looks at the clock again: a timer may not count time the
// computer spent asleep, so a long one could wake well after the end.
const longestWakeMs = 60_000

/** The warning's lead: two minutes, or half the idle time when that is shorter. */
export function warningLead(idleMs: number | null): number {
  return idleMs === null ? idleWarningLeadMs : Math.min(idleWarningLeadMs, idleMs / 2)
}

/**
 * Warns before the API ends the session for want of a request (WCAG 2.2.1): an alert dialog ahead
 * of the end that offers more time. At the end it asks the API, without renewing the session, whether
 * another tab kept it alive unheard, and signs out only if it has ended. A request from any of the
 * site's tabs puts the end back; a tab coming back into view asks the API before it trusts its end.
 */
export function IdleSessionWarning(props: {
  /** Asks the API for the session, which restarts its idle time; `ended` when the API no longer holds it. */
  readonly onStay: () => Promise<'alive' | 'ended'>
  /** Asks the API how long the session has left without renewing it, moving the end on when alive. */
  readonly onCheck: () => Promise<'alive' | 'ended'>
  /** `isSessionEnded` once the API has dropped the session, so no request answers 401 and ends this warning first. */
  readonly onSignOut: (options: { readonly isSessionEnded: boolean }) => Promise<void>
}) {
  const intl = useIntl()
  const descriptionId = useId()
  const end = useSyncExternalStore(onSessionEnd, sessionEnd)
  const lead = warningLead(sessionIdleTimeout())
  const [now, setNow] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)
  // The end for which the API could not be reached, to stay or to check; any later end clears it.
  const [unreachableAt, setUnreachableAt] = useState<number | null>(null)
  // A sign-out the person or the end started holds the dialog until it leaves the page or is answered,
  // whatever the end does meanwhile: a failed sign-out's request may itself have renewed the session.
  const [signOutState, setSignOutState] = useState<'none' | 'pending' | 'failed'>('none')
  // What screen readers were told when the warning opened, kept still while the visible countdown runs.
  const [announced, setAnnounced] = useState<{ readonly end: number; readonly seconds: number } | null>(null)

  const { onStay, onCheck, onSignOut } = props
  const stayButton = useRef<HTMLButtonElement>(null)
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const signingOut = useRef(false)
  // While Stay signed in waits for its answer, nothing signs out on its own: the answer may renew the session.
  const staying = useRef(false)
  // The end the API said had come. Only then is the session not asked to end: past this tab's own
  // end, another tab may still have kept it alive.
  const confirmedEnd = useRef<number | null>(null)
  const signOut = useCallback(async () => {
    // The end as it is now, not as this render saw it: an answer may have moved it since.
    const current = sessionEnd()
    if (signingOut.current || current === null) {
      return
    }
    signingOut.current = true
    setSignOutState('pending')
    try {
      await onSignOut({ isSessionEnded: confirmedEnd.current === current })
    } catch {
      signingOut.current = false
      setSignOutState('failed')
    }
  }, [onSignOut])

  const stay = useCallback(async () => {
    if (busy || end === null) {
      return
    }
    setBusy(true)
    staying.current = true
    setUnreachableAt(null)
    setSignOutState((state) => (state === 'failed' ? 'none' : state))
    try {
      // Answered too late: the API dropped the session first, so sign out as at the idle end, whatever
      // end an older check recorded meanwhile.
      if ((await onStay()) === 'ended' && mounted.current) {
        confirmedEnd.current = sessionEnd()
        void signOut()
      }
    } catch {
      setUnreachableAt(end)
    } finally {
      staying.current = false
      setBusy(false)
    }
  }, [busy, end, onStay, signOut])

  const pressStay = useCallback(() => void stay(), [stay])
  const pressSignOut = useCallback(() => void signOut(), [signOut])

  // The latest callbacks, so a new one, as MSAL's sign-out events bring, neither restarts the timer
  // nor drops an answer the API is still giving.
  const actions = useRef({ onCheck, signOut })
  useEffect(() => {
    actions.current = { onCheck, signOut }
  })

  // The API said the session ended while `askedFor` was the end: sign out, once for that end, unless a
  // renewal has moved the end on since or a stay may yet. After a failed sign-out, only a press of
  // Sign out or Stay signed in tries again.
  const confirmEnded = useCallback((askedFor: number) => {
    if (!mounted.current || staying.current || sessionEnd() !== askedFor || confirmedEnd.current === askedFor) {
      return
    }
    confirmedEnd.current = askedFor
    void actions.current.signOut()
  }, [])

  // Wakes at the warning, then each second until the end. A timer that wakes late, as after the
  // computer slept, still goes by the clock. At the end it asks the API once, and again while the
  // API cannot be reached. A pending stay holds it; once answered without a new end, it asks again.
  useEffect(() => {
    if (end === null) {
      return undefined
    }
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const later = (run: () => void, ms: number) => {
      timer = setTimeout(run, Math.min(Math.max(ms, 0), longestWakeMs))
    }
    // Only while this is still the end and no stay began since: either has its own run.
    const current = () => !stopped && mounted.current && sessionEnd() === end
    const expire = async () => {
      try {
        const answer = await actions.current.onCheck()
        if (!current()) {
          return
        }
        if (answer === 'ended') {
          confirmEnded(end)
        } else {
          // Alive, but the API's end is no later than this one: ask again shortly.
          later(() => void expire(), 1000)
        }
      } catch {
        if (current()) {
          setUnreachableAt(end)
          later(() => void expire(), idleCheckRetryMs)
        }
      }
    }
    const wake = () => {
      const at = Date.now()
      setNow(at)
      if (at >= end) {
        if (!busy) {
          void expire()
        }
        return
      }
      const warnAt = end - lead
      later(wake, Math.min(at < warnAt ? warnAt - at : 1000, end - at))
    }
    later(wake, end - lead - Date.now())
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [busy, confirmEnded, end, lead])

  // A tab coming back into view may have missed a renewal, or a shorter idle time, while it slept: ask
  // the API, which moves the end on if the session is alive, and sign out at once if it has ended.
  useEffect(() => {
    const ask = () => {
      const askedFor = sessionEnd()
      if (document.visibilityState !== 'visible' || askedFor === null) {
        return
      }
      const check = async () => {
        try {
          if ((await actions.current.onCheck()) === 'ended') {
            confirmEnded(askedFor)
          }
        } catch {
          // Left to the timer, which asks again at the end.
        }
      }
      void check()
    }
    document.addEventListener('visibilitychange', ask)
    window.addEventListener('pageshow', ask)
    return () => {
      document.removeEventListener('visibilitychange', ask)
      window.removeEventListener('pageshow', ask)
    }
  }, [confirmEnded])

  const seconds = end === null ? 0 : Math.max(Math.ceil((end - now) / 1000), 0)
  const inWarning = end !== null && now >= end - lead
  const unreachable = unreachableAt !== null && unreachableAt === end
  const error = signOutState === 'failed' ? 'shell.header.signOutFailed' : unreachable ? 'shell.idle.stayFailed' : null
  // Open in the warning before the end; and, at any time, while an answer is pending or has failed,
  // so either can be tried again, as after the API said the session ended long before this tab's end.
  const open = end !== null && (signOutState !== 'none' || busy || unreachable || (inWarning && now < end))

  if (open && inWarning && end !== null && announced?.end !== end) {
    setAnnounced({ end, seconds })
  }
  const announcedSeconds = announced?.end === end ? announced.seconds : seconds

  return (
    <Dialog
      open={open}
      // Closing it by keyboard or its close button is an answer too: the person is there.
      onClose={pressStay}
      isBackdropDismissible={false}
      role="alertdialog"
      descriptionId={descriptionId}
      // An alert dialog starts on the choice that keeps the person's work.
      initialFocusRef={stayButton}
      title={intl.formatMessage({ id: 'shell.idle.title' })}
      size="xs"
      actions={
        <>
          <Button variant="outlined" busy={busy} onClick={pressSignOut}>
            {intl.formatMessage({ id: 'shell.idle.signOut' })}
          </Button>
          <Button ref={stayButton} busy={busy} onClick={pressStay}>
            {intl.formatMessage({ id: 'shell.idle.stay' })}
          </Button>
        </>
      }
    >
      {inWarning && (
        <>
          {/* Read once as the warning opens; the visible countdown beside it is not read each second. */}
          <p id={descriptionId} className="sr-only">
            {intl.formatMessage({ id: 'shell.idle.body' }, { seconds: announcedSeconds })}
          </p>
          <p aria-hidden="true" className="text-sm text-text-primary">
            {intl.formatMessage({ id: 'shell.idle.body' }, { seconds })}
          </p>
          <output className="sr-only">
            {announcedSeconds > lastCallSeconds && seconds <= lastCallSeconds && seconds > 0
              ? intl.formatMessage({ id: 'shell.idle.body' }, { seconds: lastCallSeconds })
              : ''}
          </output>
        </>
      )}
      {error !== null && (
        // Describes the dialog when no countdown does, as after a failed sign-out moved the end on.
        <p id={inWarning ? undefined : descriptionId} role="alert" className="text-sm text-error-text">
          {intl.formatMessage({ id: error })}
        </p>
      )}
    </Dialog>
  )
}
